"use client";

import Image from "next/image";
import { useState, useEffect } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Skeleton } from "../ui/skeleton";
import { cn } from "@/lib/utils";
import { AccommodationImage } from "@/types/accommodations";
import { getAccommodationImageSrc } from "@/utils/getAccommodationImageSrc";

interface ThumbnailSwiperProps {
  images: AccommodationImage[];
  currentIndex: number;
  onThumbnailClick: (index: number) => void;
}

// Thumbnail image with loading state
function ThumbnailImage({
  src,
  alt,
  isActive,
  onClick,
}: {
  src: string;
  alt: string;
  isActive: boolean;
  onClick: () => void;
}) {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div
      className={cn(
        "w-[106px] md:w-[121px] h-[96px] md:h-[100px] rounded-lg border-2 overflow-hidden flex items-center transition-all duration-300 cursor-pointer",
        isActive
          ? "border-foreground scale-110 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
          : "border-transparent scale-105 hover:scale-[1.08]",
      )}
      onClick={onClick}
    >
      <div className="w-full h-full relative overflow-hidden">
        {isLoading && <Skeleton className="absolute inset-0 w-full h-full" />}
        <Image
          src={src}
          alt={alt}
          fill
          className="object-cover"
          onLoad={() => setIsLoading(false)}
        />
      </div>
    </div>
  );
}

// Inner component that uses carousel context
function ThumbnailCarouselContent({
  images,
  currentIndex,
  onThumbnailClick,
}: ThumbnailSwiperProps) {
  const { api } = useCarousel();

  // Scroll to active thumbnail when currentIndex changes
  useEffect(() => {
    if (!api) return;
    // Calculate the target scroll position to center the active thumbnail
    api.scrollTo(Math.max(0, currentIndex - 2));
  }, [api, currentIndex]);

  return (
    <CarouselContent className="-mr-4 md:-mr-4 py-3 pr-2">
      {images.map((image, index) => (
        <CarouselItem key={index} className="basis-auto pr-4 md:pr-4">
          <ThumbnailImage
            src={getAccommodationImageSrc(image?.fileKey)}
            alt={`Thumbnail ${index + 1}`}
            isActive={index === currentIndex}
            onClick={() => onThumbnailClick(index)}
          />
        </CarouselItem>
      ))}
    </CarouselContent>
  );
}

export function ThumbnailSwiper({
  images,
  currentIndex,
  onThumbnailClick,
}: ThumbnailSwiperProps) {
  return (
    <div className="h-[118px] flex items-center">
      <Carousel
        opts={{
          align: "start",
          loop: true,
          dragFree: true,
        }}
        dir="rtl"
        className="w-full pb-2"
      >
        <ThumbnailCarouselContent
          images={images}
          currentIndex={currentIndex}
          onThumbnailClick={onThumbnailClick}
        />
      </Carousel>
    </div>
  );
}
